import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON, Popup, useMap, Marker, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import ZipcodeMapSearchPanel from './ZipcodeMapSearchPanel';
import ZipcodeMapViewModeToolbar from './ZipcodeMapViewModeToolbar';
import { MAP_STYLES, ZIPCODE_MAP_VIEW } from './zipcodeMapConfig';
import {
  customerDotIcon,
  extractZipcodeAnalyticsMarkers,
  storeDotIcon,
} from './zipcodeMapAnalyticsMarkers';
import { getFeatureCoordinates, getZipFromFeature, parseCommaSeparatedZips, applyMapBoundariesApiResponse } from './zipcodeMapUtils';
import { getMapZipcodeBoundriesAPI, MAX_ZIPCODE_SEARCH } from '../../context-api/api-services/ZipcodeMap.service';
import { loadRegionalBranchZipcodeAnalytics } from '../../context-api/api-services/RegionalBranchZipcodeAnalytics.service';
import { getOnLoadRegBranchPayloadFromStorage } from '../../utils/regBranchOnLoadPayload';
import { getManagingBranchNameForApi } from '../../utils/managingBranchName';
import { buildZipcodeStatsMap } from '../../utils/regionalBranchZipcodeAnalyticsCache';
import {
  applyCacheToMapState,
  clearZipcodeMapCache,
  loadZipcodeMapCache,
  saveZipcodeMapCache,
} from '../../utils/zipcodeMapCache';

function ZipPopup({ feature, zipStatsMap }) {
  const map = useMap();
  const zip = getZipFromFeature(feature);
  const coords = getFeatureCoordinates(feature);
  const stats = zipStatsMap?.[zip];

  if (!coords) return null;

  map.setView(coords, Math.max(map.getZoom(), 12));

  return (
    <Popup position={coords}>
      <div>
        <strong>ZIP: {zip}</strong>
        <br />
        {stats ? (
          <>
            Customers: {stats.customerCount ?? 0}
            <br />
            Stores: {stats.storeCount ?? 0}
          </>
        ) : (
          <>
            Lat: {coords[0].toFixed(5)}
            <br />
            Lng: {coords[1].toFixed(5)}
          </>
        )}
      </div>
    </Popup>
  );
}

function FitMapToCoordinates({ coordinates }) {
  const map = useMap();

  useEffect(() => {
    if (!coordinates?.length) return;
    if (coordinates.length === 1) {
      map.setView(coordinates[0], 12);
      return;
    }
    const bounds = L.latLngBounds(coordinates);
    map.fitBounds(bounds, { padding: [48, 48], maxZoom: 13 });
  }, [coordinates, map]);

  return null;
}

function resolveZipcodesParam(searchMode, zipInput, branchInput) {
  if (searchMode === 'zip') {
    const zipList = parseCommaSeparatedZips(zipInput);
    if (!zipList.length) return { error: 'Enter at least one 5-digit ZIP code.' };
    if (zipList.length > MAX_ZIPCODE_SEARCH) {
      return { error: `Maximum ${MAX_ZIPCODE_SEARCH} ZIP codes per search.` };
    }
    return { zipcodesParam: zipList.join(',') };
  }

  const branch = String(branchInput ?? '').trim().toLowerCase();
  if (!branch) return { error: 'Enter a regional branch name.' };
  return { zipcodesParam: branch };
}

export default function ChicagoZipMap() {
  const sessionBranch = getOnLoadRegBranchPayloadFromStorage()?.branchName;
  const managingBranch = getManagingBranchNameForApi() || sessionBranch || '';

  const [selectedFeature, setSelectedFeature] = useState(null);
  const [popupKey, setPopupKey] = useState(0);
  const [geoJsonData, setGeoJsonData] = useState(null);
  const [searchMode, setSearchMode] = useState('zip');
  const [zipInput, setZipInput] = useState('');
  const [branchInput, setBranchInput] = useState(() => sessionBranch || '');
  const [searchResults, setSearchResults] = useState([]);
  const [notFoundZips, setNotFoundZips] = useState([]);
  const [resolvedBy, setResolvedBy] = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsError, setAnalyticsError] = useState('');
  const [mapViewMode, setMapViewMode] = useState(ZIPCODE_MAP_VIEW.ANALYTICS);

  const zipStatsMap = useMemo(() => buildZipcodeStatsMap(analytics), [analytics]);

  const isAnalyticsView = mapViewMode === ZIPCODE_MAP_VIEW.ANALYTICS;

  const { customers: customerMarkers, stores: storeMarkers } = useMemo(
    () => extractZipcodeAnalyticsMarkers(analytics),
    [analytics]
  );

  const showCustomerDots =
    mapViewMode === ZIPCODE_MAP_VIEW.CUSTOMERS || mapViewMode === ZIPCODE_MAP_VIEW.BOTH;
  const showStoreDots =
    mapViewMode === ZIPCODE_MAP_VIEW.STORES || mapViewMode === ZIPCODE_MAP_VIEW.BOTH;
  const analyticsSummary = analytics?.summary ?? null;

  const fitCoordinates = useMemo(
    () => searchResults.map((r) => r.coords).filter(Boolean),
    [searchResults]
  );

  const canReload = useMemo(() => {
    if (loading) return false;
    if (lastFetchedAt || geoJsonData) return true;
    return !resolveZipcodesParam(searchMode, zipInput, branchInput).error;
  }, [loading, lastFetchedAt, geoJsonData, searchMode, zipInput, branchInput]);

  const getFeatureStyle = useCallback(() => MAP_STYLES.highlight, []);

  const handleFeatureSelect = useCallback((feature) => {
    setSelectedFeature(feature);
    setPopupKey((key) => key + 1);
  }, []);

  const onEachFeature = useCallback((feature, layer) => {
    layer.on({
      click: () => handleFeatureSelect(feature),
    });
    layer.setStyle(MAP_STYLES.highlight);
  }, [handleFeatureSelect]);

  const applyParsedToState = useCallback((parsed, { zipcodesParam, fromCache = false, fetchedAt } = {}) => {
    if (!parsed.ok) {
      setError(parsed.message);
      setSearchResults([]);
      setGeoJsonData(null);
      setNotFoundZips(parsed.notFound ?? []);
      setResolvedBy(parsed.resolvedBy ?? null);
      setSelectedFeature(null);
      return false;
    }

    const at = fetchedAt ?? Date.now();

    setGeoJsonData(parsed.geoJson);
    setSearchResults(parsed.results);
    setNotFoundZips(parsed.notFound ?? []);
    setResolvedBy(parsed.resolvedBy ?? null);
    setSelectedFeature(parsed.results[0]?.feature ?? null);
    setLastFetchedAt(at);
    setError('');

    if (!fromCache) {
      saveZipcodeMapCache({
        fetchedAt: at,
        searchMode,
        zipInput,
        branchInput,
        zipcodesParam,
        geoJson: parsed.geoJson,
        results: parsed.results,
        notFound: parsed.notFound ?? [],
        resolvedBy: parsed.resolvedBy ?? null,
      });
    }

    return true;
  }, [searchMode, zipInput, branchInput]);

  const applySearchResponse = useCallback(
    (res, zipcodesParam) => {
      const parsed = applyMapBoundariesApiResponse(res);
      return applyParsedToState(parsed, { zipcodesParam });
    },
    [applyParsedToState]
  );

  useEffect(() => {
    const cached = loadZipcodeMapCache();
    if (!cached) return;

    const restored = applyCacheToMapState(cached);
    if (!restored) return;

    setSearchMode(restored.searchMode);
    setZipInput(restored.zipInput);
    setBranchInput(restored.branchInput);
    setGeoJsonData(restored.geoJson);
    setSearchResults(restored.results);
    setNotFoundZips(restored.notFound);
    setResolvedBy(restored.resolvedBy);
    setLastFetchedAt(restored.fetchedAt);
    setSelectedFeature(restored.results[0]?.feature ?? null);
    setError('');
  }, []);

  useEffect(() => {
    if (!managingBranch) return;
    let cancelled = false;

    (async () => {
      const result = await loadRegionalBranchZipcodeAnalytics(managingBranch);
      if (cancelled) return;
      if (result.ok) {
        setAnalytics(result.data);
        setAnalyticsError('');
      } else {
        setAnalytics(null);
        setAnalyticsError(result.error || 'Could not load zipcode analytics.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [managingBranch]);

  const fetchBoundaries = useCallback(async () => {
    const resolved = resolveZipcodesParam(searchMode, zipInput, branchInput);
    if (resolved.error) {
      setError(resolved.error);
      return;
    }

    setError('');
    setNotFoundZips([]);
    setResolvedBy(null);
    setLoading(true);

    try {
      const res = await getMapZipcodeBoundriesAPI({ zipcodes: resolved.zipcodesParam });
      applySearchResponse(res, resolved.zipcodesParam);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Search failed. Try again.');
    } finally {
      setLoading(false);
    }
  }, [searchMode, zipInput, branchInput, applySearchResponse]);

  const handleSearch = () => fetchBoundaries();

  const handleReload = () => fetchBoundaries();

  const handleClear = () => {
    setZipInput('');
    setSearchResults([]);
    setNotFoundZips([]);
    setResolvedBy(null);
    setLastFetchedAt(null);
    setError('');
    setSelectedFeature(null);
    setBranchInput(sessionBranch || '');
    setGeoJsonData(null);
    clearZipcodeMapCache();
  };

  return (
    <div style={{ height: '90vh', position: 'relative' }}>
      <div
        style={{
          position: 'absolute',
          top: 30,
          left: 50,
          zIndex: 1000,
          width: 'calc(100% - 32px)',
          maxWidth: 360,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          pointerEvents: 'none',
        }}
      >
        <ZipcodeMapViewModeToolbar mapViewMode={mapViewMode} onMapViewModeChange={setMapViewMode} />
        <ZipcodeMapSearchPanel
        searchMode={searchMode}
        onSearchModeChange={setSearchMode}
        zipInput={zipInput}
        onZipInputChange={setZipInput}
        branchInput={branchInput}
        onBranchInputChange={setBranchInput}
        onSearch={handleSearch}
        onReload={handleReload}
        onClear={handleClear}
        loading={loading}
        error={error}
        searchResults={searchResults}
        notFoundZips={notFoundZips}
        resolvedBy={resolvedBy}
        mappedCount={searchResults.length}
        lastFetchedAt={lastFetchedAt}
        canReload={canReload}
        analyticsSummary={analyticsSummary}
        analyticsError={analyticsError}
        zipStatsMap={zipStatsMap}
        />
      </div>

      {sessionBranch && searchMode === 'branch' && (
        <BoxHint branchName={sessionBranch} />
      )}

      <MapContainer
        center={[42.03, -87.7]}
        zoom={11}
        scrollWheelZoom
        dragging
        doubleClickZoom
        zoomControl
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />
        {geoJsonData && (
          <GeoJSON
            key={geoJsonData.features?.length ?? 0}
            data={geoJsonData}
            style={getFeatureStyle}
            onEachFeature={onEachFeature}
          />
        )}
        {isAnalyticsView && selectedFeature && (
          <ZipPopup key={popupKey} feature={selectedFeature} zipStatsMap={zipStatsMap} />
        )}
        {fitCoordinates.length > 0 && <FitMapToCoordinates coordinates={fitCoordinates} />}
        {searchResults.map(({ zip, coords }) => (
          <Marker key={`label-${zip}`} position={coords} interactive={false} opacity={0}>
            <Tooltip direction="center" offset={[0, 0]} permanent>
              {zip}
            </Tooltip>
          </Marker>
        ))}
        {showCustomerDots &&
          customerMarkers.map((item, index) => (
            <Marker
              key={`customer-${item.zip}-${item.email ?? index}-${item.position.join(',')}`}
              position={item.position}
              icon={customerDotIcon}
            >
              <Popup>
                <AnalyticsEntityPopup title="Customer" item={item} />
              </Popup>
            </Marker>
          ))}
        {showStoreDots &&
          storeMarkers.map((item, index) => (
            <Marker
              key={`store-${item.zip}-${item.email ?? item.name ?? index}-${item.position.join(',')}`}
              position={item.position}
              icon={storeDotIcon}
            >
              <Popup>
                <AnalyticsEntityPopup title="Store" item={item} />
              </Popup>
            </Marker>
          ))}
      </MapContainer>
    </div>
  );
}

function AnalyticsEntityPopup({ title, item }) {
  return (
    <div>
      <strong>{title}</strong>
      <br />
      ZIP: {item.zip}
      {item.name ? (
        <>
          <br />
          {item.name}
        </>
      ) : null}
      {item.email ? (
        <>
          <br />
          {item.email}
        </>
      ) : null}
      {item.address ? (
        <>
          <br />
          {item.address}
        </>
      ) : null}
    </div>
  );
}

function BoxHint({ branchName }) {
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 16,
        left: 16,
        zIndex: 1000,
        background: 'rgba(255,255,255,0.92)',
        padding: '6px 12px',
        borderRadius: 8,
        fontSize: 11,
        color: '#6B7280',
        fontFamily: 'Inter Regular, sans-serif',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}
    >
      Logged in branch: {branchName}
    </div>
  );
}
